import { BlockTypes } from "./block-types";
import { ConnectPointClickEvent } from "./connectors";
import { MouseEvent } from "react";

export const ROOT_BLOCK_ID = "root";

export const AUTO_SCALING_GROUP_SCALE_UP_THRESHOLD = 0.8;
export const AUTO_SCALING_GROUP_SCALE_DOWN_THRESHOLD = 0.3;
export const QUEUE_MAX_STORAGE = 1000000;
export const PASSTHROUGH_INFINITE_CAPACITY = Number.MAX_SAFE_INTEGER;

export interface ScreenPosition {
  top: number;
  left: number;
}

export interface Block extends ScreenPosition {
  id: string;
  width: number;
  height: number;
  inputConnectors?: string[];
  outputConnectors?: string[];
  isSelected?: boolean;
  blockType: BlockTypes;
  inputCapacity: IOPS;
  processingCapacity: IOPS;
  // optional maximum processing capacity for blocks that can scale their processing (e.g., Cache)
  maxProcessingCapacity?: IOPS;
  dynamicProperties?: {
    [key: string]: any;
  };
  costPerCycle?: number;
  /** User-defined custom label for the block on the canvas */
  customName?: string;
}

export interface BlockPositionMap {
  [key: string]: Block;
}

export interface BlockProps extends Block {
  onConnectorClick: (event: ConnectPointClickEvent) => void;
  onMouseDown: (event: BlockMouseDownEvent) => void;
  onMouseUp: () => void;
  onDeleteClick: (e: MouseEvent<HTMLButtonElement>, blockID: string) => void;
  startBlock?: ConnectPointClickEvent | null;
  inputBlocks?: string[];
  outputBlocks?: string[];
  gameIsStarted: boolean;
}

export interface BlockMouseDownEvent {
  diff: ScreenPosition;
  blockID: string;
}

export enum OperationTypes {
  READ = "read",
  WRITE = "write",
}

export enum ContentTypes {
  STATIC = "static",
  DYNAMIC = "dynamic",
  STATIC_MEDIA = "static_media",
}

export interface ContentIOPSCountMap {
  [key: string]: number;
}

export interface ContentIOPS {
  total: number;
  byContentType: ContentIOPSCountMap;
}

export interface OperationTypeMap {
  [key: string]: ContentIOPS;
}

export interface IOPS {
  byOperationType: OperationTypeMap;
  total: number;
}

export interface IOPSCapacity extends IOPS {
  maxConcurrentIOPS: number;
}

export interface IOPSDistribution {
  readStatic: number;
  readDynamic: number;
  writeDynamic: number;
}

export interface BlockIOPS {
  blockID: string;
  inputIOPS: IOPS;
  outputIOPS: IOPS;
  percentageSuccess: number;
}

export interface FlattendIOPS {
  total: number;
  [key: string]: number; // operation type as key, e.g., 'read', 'write'
}

export interface ConnectorLimitMap {
  [key: string]: number;
}

export const initialRootIOPS: IOPS = {
  total: 20,
  byOperationType: {
    [`${OperationTypes.READ}`]: {
      total: 10,
      byContentType: {
        [`${ContentTypes.DYNAMIC}`]: 5,
        [`${ContentTypes.STATIC}`]: 5,
        [`${ContentTypes.STATIC_MEDIA}`]: 0,
      },
    },
    [`${OperationTypes.WRITE}`]: {
      total: 10,
      byContentType: {
        [`${ContentTypes.DYNAMIC}`]: 10,
        [`${ContentTypes.STATIC}`]: 0,
        [`${ContentTypes.STATIC_MEDIA}`]: 0,
      },
    },
  },
};

export function zeroIOPS(): IOPS {
  return {
    total: 0,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 0,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 0,
          [`${ContentTypes.STATIC}`]: 0,
          [`${ContentTypes.STATIC_MEDIA}`]: 0,
        },
      },
      [`${OperationTypes.WRITE}`]: {
        total: 0,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 0,
          [`${ContentTypes.STATIC}`]: 0,
          [`${ContentTypes.STATIC_MEDIA}`]: 0,
        },
      },
    },
  };
}

export function zeroIOPSCapacity(): IOPSCapacity {
  return {
    total: 0,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 0,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 0,
          [`${ContentTypes.STATIC}`]: 0,
          [`${ContentTypes.STATIC_MEDIA}`]: 0,
        },
      },
      [`${OperationTypes.WRITE}`]: {
        total: 0,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 0,
          [`${ContentTypes.STATIC}`]: 0,
          [`${ContentTypes.STATIC_MEDIA}`]: 0,
        },
      },
    },
    maxConcurrentIOPS: 0,
  };
}

export interface BlockIOPSMap {
  [key: string]: BlockIOPS;
}

export interface BlockConfig {
  displayName: string;
  imagePath: string;
  isInitiator: boolean;
  isTerminator: boolean;
  isInterceptor: boolean;
  allowedInputTypes: Set<BlockTypes>;
  allowedOutputTypes: Set<BlockTypes>;
  inputCapacity: IOPS;
  processingCapacity: IOPS;
  // optional maximum processing capacity for blocks that support scaling
  maxProcessingCapacity?: IOPS;
  purchasePrice: number;
  sellPrice: number;
  costPerCycle: number;
  outputConnectorLimits: ConnectorLimitMap;
  blockSideEffects?: (current: Block, iops: IOPS) => Block;
  hasSettings?: boolean;
}

export interface BlockConfigMap {
  [key: string]: BlockConfig;
}

// A function that multiplies the IOPS object by the given multiplier
export function multiplyIOPS(iops: IOPS, multiplier: number): IOPS {
  const result: IOPS = {
    ...zeroIOPS(),
    total: iops.total * multiplier,
  };

  // loop over all operation types and multiply each value by the multiplier
  Object.keys(iops.byOperationType).forEach((opTypeKey: string) => {
    const opType = iops.byOperationType[`${opTypeKey}`];
    if (opType) {
      // loop over all content types
      let operationTotal = 0;
      Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
        // get current request count
        const contentTypeIOP = opType.byContentType[contentTypeKey];
        if (contentTypeIOP !== undefined && contentTypeIOP !== null) {
          // multiply the value by the multiplier
          const nextVal = contentTypeIOP * multiplier;
          // update content level counts
          const nextContentIOP: ContentIOPSCountMap = {
            ...result.byOperationType[`${opTypeKey}`].byContentType,
            [`${contentTypeKey}`]: nextVal,
          };

          result.byOperationType[`${opTypeKey}`] = {
            ...result.byOperationType[`${opTypeKey}`],
            byContentType: nextContentIOP,
          };

          operationTotal += nextVal;
        }
      });

      // update operation level total
      result.byOperationType[`${opTypeKey}`] = {
        ...result.byOperationType[`${opTypeKey}`],
        total: operationTotal,
      };
    }
  });

  return result;
}

// A function that flattens the IOPS object by removing the byOperationType and byContentType properties
// and returning a flat map of operation types to their total IOPS
export const flattenIOPS = (iops: IOPS): FlattendIOPS => {
  const flatIOPS: FlattendIOPS = { total: iops.total };
  Object.keys(iops.byOperationType).forEach((opTypeKey: string) => {
    const opType = iops.byOperationType[`${opTypeKey}`];
    if (opType) {
      iops.byOperationType[`${opTypeKey}`].byContentType &&
        Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
          const contentTypeIOP = opType.byContentType[contentTypeKey];
          if (contentTypeIOP !== undefined && contentTypeIOP !== null) {
            // add the content type IOPS to the flat map
            flatIOPS[`${opTypeKey}-${contentTypeKey}`] = contentTypeIOP;
          }
        });
    }
  });
  return flatIOPS;
};

// new helper: element-wise minimum of two IOPS objects (keeps structure of IOPS)
export function minIOPS(iops1: IOPS, iops2: IOPS): IOPS {
  const result: IOPS = {
    ...zeroIOPS(),
    total: Math.min(iops1.total || 0, iops2.total || 0),
  };

  Object.keys(iops1.byOperationType).forEach((opTypeKey: string) => {
    const opType1 = iops1.byOperationType[`${opTypeKey}`];
    const opType2 = iops2.byOperationType ? iops2.byOperationType[`${opTypeKey}`] : undefined;
    if (!opType1) return;

    let operationTotal = 0;
    Object.keys(opType1.byContentType).forEach((contentTypeKey: string) => {
      const val1 = opType1.byContentType[contentTypeKey] || 0;
      const val2 =
        opType2 && opType2.byContentType && opType2.byContentType[contentTypeKey]
          ? opType2.byContentType[contentTypeKey]
          : 0;
      const nextVal = Math.min(val1, val2);

      result.byOperationType[`${opTypeKey}`].byContentType[contentTypeKey] = nextVal;
      operationTotal += nextVal;
    });

    result.byOperationType[`${opTypeKey}`] = {
      ...result.byOperationType[`${opTypeKey}`],
      total: operationTotal,
    };
  });

  return result;
}
